import { useCallback, useEffect } from "react";
import { useDispatch as useReduxDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { pasteModelTask } from "services/models/modelDetails/modelTasks";

// Sole owner of the "task" clipboard storage format - read and write both
// live here so they can't drift apart. The copy has to survive a full page
// navigation (copy on one model, paste on another), which is what makes
// localStorage the right tool here rather than the query cache - but unlike
// the old taskInfo bug, the entry is always cleared once consumed, so a
// stale clipboard can never be silently pasted twice.
const TASK_CLIPBOARD_KEY = "task";

const readCopiedTask = () => {
	try {
		return JSON.parse(localStorage.getItem(TASK_CLIPBOARD_KEY));
	} catch {
		return null;
	}
};

// dispatch here is the screen's local reducer dispatch (showPasteTask,
// showAdd, ...), not Redux - paste errors go through Redux's showError
// separately, since that's the app-wide toast mechanism.
const useTaskCopyPaste = ({ modelId, refresh, showPasteTask, dispatch }) => {
	const reduxDispatch = useReduxDispatch();

	const handleCopy = useCallback(
		(modelTaskId) => {
			localStorage.setItem(
				TASK_CLIPBOARD_KEY,
				JSON.stringify({ fromTask: true, modelTaskId })
			);
			dispatch({ type: "DISABLE_PASTE_TASK", payload: false });
		},
		[dispatch]
	);

	// isPasteTaskDisabled lives in this screen's local reducer, so it resets to its
	// initial (disabled) value on every fresh mount - including navigating from the
	// model that was copied from to the model it should be pasted into. Re-derive it
	// from the clipboard (which is deliberately global, see above) on mount and again
	// whenever the tab regains focus, so a pending cross-model copy stays pasteable.
	useEffect(() => {
		const rehydrate = () => {
			const copiedTask = readCopiedTask();
			if (copiedTask?.fromTask && copiedTask?.modelTaskId) {
				dispatch({ type: "DISABLE_PASTE_TASK", payload: false });
			}
		};

		rehydrate();

		const onVisibilityChange = () => {
			if (!document.hidden) rehydrate();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, [dispatch]);

	useEffect(() => {
		if (!showPasteTask) return;
		let cancelled = false;

		const paste = async () => {
			const copiedTask = readCopiedTask();
			if (!copiedTask?.modelTaskId) {
				if (!cancelled) reduxDispatch(showError("Nothing to paste"));
				dispatch({ type: "TOGGLE_PASTE_TASK", payload: false });
				return;
			}
			const response = await pasteModelTask(modelId, {
				ModelVersionTaskID: Number(copiedTask.modelTaskId),
			});
			if (cancelled) return;
			if (!response?.status) {
				reduxDispatch(showError(response?.data?.detail || "Failed to paste task"));
				dispatch({ type: "TOGGLE_PASTE_TASK", payload: false });
				return;
			}
			localStorage.removeItem(TASK_CLIPBOARD_KEY);
			await refresh();
			if (!cancelled) dispatch({ type: "TOGGLE_PASTE_TASK", payload: false });
		};

		paste();
		return () => {
			cancelled = true;
		};
	}, [dispatch, modelId, reduxDispatch, refresh, showPasteTask]);

	return { handleCopy };
};

export default useTaskCopyPaste;
